/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { SubdivisionFormData, SubdivisionSchema } from "core/schema";
import { admin_subdivisions_path, admin_subdivision_path } from "routes";

type SubdivisionFormProps = {
  subdivision: SubdivisionFormData;
  policy?: Record<string, boolean>;
};

const SubdivisionForm = ({ subdivision }: SubdivisionFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<SubdivisionFormData>({
    mode: "onChange",
    defaultValues: SubdivisionSchema.cast(subdivision),
    resolver: yupResolver(SubdivisionSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: SubdivisionFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_subdivision_path(subdivision), data);
      } else {
        await api.post(admin_subdivisions_path(subdivision), data);
      }

      navigateTo(admin_subdivisions_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={subdivision.id ? "Edit Subdivision" : "New Subdivision"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_subdivisions_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={6} className="px-4">
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input
                    {...register("code")}
                    label="Code"
                    required
                    errorMessage={errors.code?.message}
                    className="field text-uppercase"
                    placeholder="LTS"
                    maxLength={3}
                  />
                  <Input
                    {...register("name")}
                    label="Name"
                    required
                    errorMessage={errors.name?.message}
                    placeholder="La Tierra Solana"
                    maxLength={100}
                  />
                  <Input
                    {...register("address")}
                    label="Address"
                    required
                    errorMessage={errors.address?.message}
                    type="textarea"
                    placeholder="Sitio Banlic, Bacolor, Pampanga"
                    maxLength={255}
                  />
                </Col>
                <Col lg={6} className="px-4">
                  <Row className="pt-16 justify-content-center">
                    <Col>
                      <Input
                        {...register("phase_start")}
                        label="Phase start"
                        required
                        errorMessage={errors.phase_start?.message}
                        placeholder="1"
                        type="number"
                        maxLength={100}
                      />
                    </Col>
                    <Col>
                      <Input
                        {...register("phase_end")}
                        label="Phase End"
                        required
                        errorMessage={errors.phase_end?.message}
                        placeholder="4"
                        type="number"
                        maxLength={100}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Input
                        {...register("block_start")}
                        label="Block Start"
                        required
                        errorMessage={errors.block_start?.message}
                        placeholder="1"
                        type="number"
                        maxLength={100}
                      />
                    </Col>
                    <Col>
                      <Input
                        {...register("block_end")}
                        label="Block End"
                        required
                        errorMessage={errors.block_end?.message}
                        placeholder="999"
                        type="number"
                        maxLength={100}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Input
                        {...register("lot_start")}
                        label="Lot Start"
                        required
                        errorMessage={errors.lot_start?.message}
                        placeholder="99"
                        maxLength={100}
                      />
                    </Col>
                    <Col>
                      <Input
                        {...register("lot_end")}
                        label="Lot End"
                        required
                        errorMessage={errors.lot_end?.message}
                        placeholder="999"
                        maxLength={100}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default SubdivisionForm;
